import { DotsVerticalIcon, Trash01Icon } from '@langchain/untitled-ui-icons';
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useState } from 'react';

import { DataGridColumnVisibilityPopover } from '@/components/DataGrid/DataGridColumnVisibilityPopover';
import { FetchError } from '@/data/fetcher';
import { IconButton } from '@/design-system';
import {
  GridPaginationModel,
  useDataGridState,
} from '@/hooks/useDataGridState';
import { usePermissions } from '@/hooks/usePermissions';
import { useHostListeners } from '@/hooks/useSwr';
import { HostListenerSchema } from '@/types/schema';
import { hostApiPathV2 } from '@/utils/constants';

import { DataGrid, DataGridPagination } from '../DataGrid';
import { useDataGridSizingLocalStorage } from '../DataGrid.utils';
import { DeleteModal } from '../Delete';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown';
import { ErrorBanner } from '../ErrorBanner';
import { TableCellTextOverflow } from '../Table';
import { TextOverflowTooltip } from '../TextOverflowTooltip';

const columnHelper = createColumnHelper<HostListenerSchema>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const COLUMNS_DEF: ColumnDef<HostListenerSchema, any>[] = [
  columnHelper.accessor('id', {
    header: 'Listener ID',
    cell: ({ row }) => (
      <TextOverflowTooltip className="text-primary">
        {row.original.id}
      </TextOverflowTooltip>
    ),
    enableResizing: true,
    enableSorting: false,
    size: 300,
  }),
  columnHelper.accessor('compute_id', {
    header: 'Compute ID',
    cell: ({ row }) => (
      <TextOverflowTooltip className="text-primary">
        {row.original.compute_id}
      </TextOverflowTooltip>
    ),
    enableResizing: true,
    enableSorting: false,
    size: 300,
  }),
  columnHelper.accessor('version', {
    header: 'Version',
    cell: ({ row }) => (
      <TextOverflowTooltip className="text-primary">
        {row.original.version || 'N/A'}
      </TextOverflowTooltip>
    ),
    enableResizing: true,
    enableSorting: false,
    size: 120,
  }),
  columnHelper.accessor('compute_config.k8s_namespaces', {
    header: 'K8s Namespaces',
    cell: ({ row }) => (
      <TextOverflowTooltip className="text-primary">
        {row.original.compute_config.k8s_namespaces?.join(', ') || 'N/A'}
      </TextOverflowTooltip>
    ),
    enableResizing: true,
    enableSorting: false,
    size: 200,
  }),
  columnHelper.accessor('updated_at', {
    header: 'Updated At',
    cell: ({ row }) => new Date(row.original.updated_at).toLocaleString(),
    enableResizing: true,
    enableSorting: false,
    size: 180,
  }),
  columnHelper.display({
    id: 'actions',
    cell: ({ row, table }) => {
      const { actions } = (table.options.meta ?? {}) as {
        actions: {
          handleDelete: (id: string) => void;
          deleteEnabled: boolean;
        };
      };

      if (!actions.deleteEnabled) return null;

      return (
        <TableCellTextOverflow className="justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                icon={DotsVerticalIcon}
                label="Actions"
                color="secondary"
                variant="plain"
                className="rounded-bl-none rounded-tl-none border-l-0"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                onClick={() => actions.handleDelete(row.original.id)}
                className="flex items-center gap-2"
              >
                <Trash01Icon className="h-5 w-5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCellTextOverflow>
      );
    },
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    size: 40,
  }),
];

const EMPTY_TABLE = [];

const COLUMN_VISIBILITY_STATE_KEY = 'ls:ui:listeners-table-columns';

type SimpleTableProps =
  | {
      simple: true;
      defaultPaginationModel: GridPaginationModel;
    }
  | {
      simple?: false;
    };

export const HostListenersTable = (props: SimpleTableProps) => {
  'use no memo';
  const { simple } = props;
  const { authorize } = usePermissions();

  const {
    paginationModel,
    setPaginationModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
  } = useDataGridState({
    columnVisibilityStateKey: COLUMN_VISIBILITY_STATE_KEY,
  });
  const mPaginationModel = simple
    ? props.defaultPaginationModel
    : paginationModel;

  const {
    data: rows,
    isLoading,
    isValidating,
    error,
  } = useHostListeners({
    offset: mPaginationModel.pageIndex * mPaginationModel.pageSize,
    limit: mPaginationModel.pageSize,
  });

  const [selectedToDeleteId, setSelectedToDeleteId] = useState<string | null>(
    null
  );

  const [columnSizing, setColumnSizing] =
    useDataGridSizingLocalStorage('hostListeners');

  const table = useReactTable({
    columns: COLUMNS_DEF,
    data: rows?.resources ?? EMPTY_TABLE,

    state: {
      pagination: mPaginationModel,
      columnSizing,
      columnVisibility: columnVisibilityModel,
    },
    meta: {
      actions: {
        handleDelete: setSelectedToDeleteId,
        deleteEnabled: authorize('deployments:delete'),
      },
    },
    onPaginationChange: setPaginationModel,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibilityModel,
    getRowId: (row) => row.id,

    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),

    manualExpanding: true,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    columnResizeMode: 'onChange',

    pageCount: Math.ceil(+(rows?.offset || 0) / mPaginationModel.pageSize),
  });

  if (error instanceof FetchError) {
    return (
      <ErrorBanner>
        {error.status === 401
          ? 'Permission denied, you do not have the required permission deployments:read'
          : error.message}
      </ErrorBanner>
    );
  }

  const NoListeners = () => (
    <div className="flex h-64 items-center justify-center">
      <div className="text-center">
        <p className="text-lg font-medium text-secondary">No listeners found</p>
        <p className="mt-2 text-sm text-tertiary">
          Get started by creating your first listener
        </p>
      </div>
    </div>
  );

  return (
    <>
      {!simple && (
        <div className="mb-2 flex justify-end">
          <DataGridColumnVisibilityPopover
            table={table}
            localStorageKey={COLUMN_VISIBILITY_STATE_KEY}
          />
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-secondary">
        <DataGrid
          table={table}
          cellHeight={64}
          cellSpacingVariant="wide"
          isLoading={isLoading || isValidating}
          emptyState={<NoListeners />}
          stickyRightColumn={true}
        />
      </div>

      {!simple && <DataGridPagination table={table} />}

      <DeleteModal
        endpoint={`${hostApiPathV2}/listeners`}
        id={selectedToDeleteId as string}
        isOpen={!!selectedToDeleteId}
        doClose={() => setSelectedToDeleteId(null)}
        modalTitle={`Delete listener ${
          rows?.resources.find((row) => row.id === selectedToDeleteId)?.id
        }?`}
        modalText="Deleting a listener will permanently remove it from your organization. This action cannot be undone."
      />
    </>
  );
};
